import { useState } from "react";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, useApiData, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTitle, EmptyState } from "@/components/shell/DashboardShell";
import { AttachmentList, DetailGrid } from "@/components/portal/RequestParts";
import { FilterBar, Pill, useQueryPath } from "@/components/admin/FilterBar";

const VendorDialog = ({ v, onClose, onSaved }) => {
  const [note, setNote] = useState(v?.review_note || "");
  const [busy, setBusy] = useState("");
  if (!v) return null;
  const act = async (status) => {
    setBusy(status);
    try {
      const { data } = await api.patch(`/admin/vendors/${v.id}`, { status, note });
      toast.success(`${v.company_name} ${status}`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="vendor-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-3">{v.company_name} <StatusBadge status={v.status} /></DialogTitle></DialogHeader>
        {v.free_email && (
          <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="vendor-free-email-warning"><AlertTriangle className="h-4 w-4" /> Registered with a free email domain — verify company authenticity.</p>
        )}
        <DetailGrid rows={[["GSTIN / Reg. no.", v.gst_number], ["Company type", v.company_type], ["Industry", v.industry], ["Website", v.website], ["Employees", v.employee_count], ["Sites", v.sites_count], ["Contact", `${v.contact_name} (${v.designation})`], ["Email", v.email], ["Phone", v.phone], ["Address", `${v.address}, ${v.city}, ${v.state} ${v.pincode}`], ["Interests", v.interests], ["Registered", fmtDate(v.created_at)]]} />
        <div><p className="mb-2 text-sm font-semibold">Verification documents</p><AttachmentList files={v.attachments} /></div>
        <div>
          <p className="mb-2 text-sm font-semibold">Review note (included in rejection email)</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="vendor-review-note" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => act("approved")} disabled={!!busy} className="btn-primary bg-emerald-600 hover:bg-emerald-700" data-testid="vendor-approve-button">{busy === "approved" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve</button>
          <button onClick={() => act("rejected")} disabled={!!busy} className="btn-outline text-red-700" data-testid="vendor-reject-button">{busy === "rejected" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Reject</button>
          {v.status !== "pending" && <button onClick={() => act("pending")} disabled={!!busy} className="btn-outline" data-testid="vendor-pending-button">Move to pending</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminVendors() {
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const { data, loading, reload } = useApiData(useQueryPath("/admin/vendors", { status, search }));
  const rows = data || [];
  return (
    <div data-testid="admin-vendors-page">
      <PageTitle eyebrow="Registrations" title="Vendor approvals" text="Verify company details before granting portal access." />
      <FilterBar search={search} setSearch={setSearch} testid="vendors-filter">
        {["pending", "approved", "rejected", "all"].map((s) => <Pill key={s} active={status === s} onClick={() => setStatus(s)} testid={`vendors-filter-${s}`}>{s[0].toUpperCase() + s.slice(1)}</Pill>)}
      </FilterBar>
      {!loading && !rows.length ? <EmptyState title="No registrations" text="Nothing matches this filter." testid="vendors-empty" /> : (
        <div className="card-base overflow-x-auto" data-testid="vendors-table">
          <Table>
            <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>GSTIN</TableHead><TableHead>Contact</TableHead><TableHead>City</TableHead><TableHead>Registered</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.id} className="cursor-pointer" onClick={() => setSel(v)} data-testid={`vendor-row-${v.id}`}>
                  <TableCell className="font-medium">{v.company_name}{v.free_email && <AlertTriangle className="ml-2 inline h-3.5 w-3.5 text-amber-500" />}</TableCell>
                  <TableCell className="mono text-xs">{v.gst_number}</TableCell>
                  <TableCell className="text-sm">{v.contact_name}<div className="text-xs text-slate-500">{v.email}</div></TableCell>
                  <TableCell className="text-sm">{v.city}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(v.created_at)}</TableCell>
                  <TableCell><StatusBadge status={v.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {sel && <VendorDialog v={sel} onClose={() => setSel(null)} onSaved={() => { setSel(null); reload(); }} />}
    </div>
  );
}
